import { useEffect, useRef, useState } from "react";
import { Sheet } from "../components/Sheet";
import {
  createEvent,
  espnLabel,
  fetchAddOptions,
  fetchEspnEvent,
  missingFields,
  searchEspnGames,
  type AddOptions,
  type EspnGame,
  type ImportableGame,
  type NewEvent,
} from "../lib/addEvent";

const NEW_LEAGUE = "__new__";
const input = "min-w-0 rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100";
const labelCls = "grid gap-1 text-xs text-muted";

function Text({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className={labelCls}>
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={input} />
    </label>
  );
}

function Choice({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <label className={labelCls}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className={input}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface Draft {
  home_team: string;
  away_team: string;
  home_score: string;
  away_score: string;
  has_score: boolean;
  venue_name: string;
  venue_city: string;
  notes: string;
}

const EMPTY: Draft = { home_team: "", away_team: "", home_score: "0", away_score: "0", has_score: true, venue_name: "", venue_city: "", notes: "" };

function draftFrom(g: EspnGame | ImportableGame): Draft {
  const s = "home_score" in g ? g : null;
  return {
    home_team: g.home_team,
    away_team: g.away_team,
    home_score: String(s?.home_score ?? 0),
    away_score: String(s?.away_score ?? 0),
    // The server's has_final_score verdict, never a browser copy of the rule.
    has_score: s ? s.has_final_score : false,
    venue_name: g.venue_name ?? "",
    venue_city: g.venue_city ?? "",
    notes: "notes" in g ? (g.notes ?? "") : "",
  };
}

/** The teams, score, venue and notes block every path ends in. */
function GameFields({ d, set }: { d: Draft; set: (d: Draft) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <Text label="Home team" value={d.home_team} onChange={(v) => set({ ...d, home_team: v })} />
      <Text label="Away team" value={d.away_team} onChange={(v) => set({ ...d, away_team: v })} />
      <label className="col-span-2 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={d.has_score} onChange={(e) => set({ ...d, has_score: e.target.checked })} />
        Has a final score
      </label>
      {d.has_score && (
        <>
          <Text label="Home score" type="number" value={d.home_score} onChange={(v) => set({ ...d, home_score: v })} />
          <Text label="Away score" type="number" value={d.away_score} onChange={(v) => set({ ...d, away_score: v })} />
        </>
      )}
      <Text label="Venue *" value={d.venue_name} onChange={(v) => set({ ...d, venue_name: v })} />
      <Text label="City *" value={d.venue_city} onChange={(v) => set({ ...d, venue_city: v })} />
      <label className={`${labelCls} col-span-2`}>
        Notes (optional)
        <textarea value={d.notes} onChange={(e) => set({ ...d, notes: e.target.value })} rows={2} className={input} />
      </label>
    </div>
  );
}

/**
 * Add event: import a watchlist game, or pick a date and league and either
 * tap an ESPN game or enter it by hand, with manual entry behind a link
 * instead of a second always-open form.
 */
export default function AddEventSheet({ onClose, onSaved }: { onClose: () => void; onSaved: (warning: string | null) => void }) {
  const [opts, setOpts] = useState<AddOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importId, setImportId] = useState("");
  const [date, setDate] = useState("");
  const [league, setLeague] = useState("NHL");
  const [newLeague, setNewLeague] = useState("");
  const [sport, setSport] = useState("Hockey");
  const [gameType, setGameType] = useState("Regular Season");
  const [special, setSpecial] = useState("");
  const [results, setResults] = useState<EspnGame[] | null>(null);
  const [picked, setPicked] = useState<EspnGame | null>(null);
  const [manual, setManual] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  // ESPN's current view of the watchlist game being imported, and which pick
  // it answers, so a slow reply for an earlier pick cannot fill in a later one.
  const [live, setLive] = useState<EspnGame | null>(null);
  const importRef = useRef("");

  useEffect(() => {
    fetchAddOptions()
      .then((o) => {
        setOpts(o);
        setDate(o.today);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (!opts) {
    return (
      <Sheet title="Add event" onClose={onClose}>
        {error ? <p className="text-sm text-bad">{error}</p> : <p className="text-sm text-muted">Loading…</p>}
      </Sheet>
    );
  }

  const imported = opts.importable.find((g) => String(g.id) === importId) ?? null;
  const leagueName = league === NEW_LEAGUE ? newLeague.trim() : league;
  const espnOk = league !== NEW_LEAGUE && opts.espn_leagues.includes(league);
  const showForm = imported !== null || picked !== null || manual || !espnOk;

  async function search() {
    setBusy(true);
    setError(null);
    try {
      setResults(await searchEspnGames(league, date));
      setPicked(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setResults([]);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const problem = missingFields(draft);
    if (problem) return setError(problem);
    if (!leagueName) return setError("Enter a league name.");
    const body: NewEvent = {
      source: imported ? "upcoming" : picked ? "espn" : "manual",
      event_date: imported && !date ? imported.event_date : date,
      league: imported ? imported.league : leagueName,
      sport: league === NEW_LEAGUE ? sport : undefined,
      game_type: gameType,
      special,
      home_team: draft.home_team.trim(),
      away_team: draft.away_team.trim(),
      home_score: draft.has_score ? Number(draft.home_score) || 0 : null,
      away_score: draft.has_score ? Number(draft.away_score) || 0 : null,
      has_score: draft.has_score,
      venue_name: draft.venue_name.trim(),
      venue_city: draft.venue_city.trim(),
      notes: draft.notes.trim(),
      espn_event_id: imported ? imported.espn_event_id : picked?.espn_event_id ?? null,
      upcoming_id: imported?.id,
    };
    setBusy(true);
    setError(null);
    try {
      const saved = await createEvent(body);
      onSaved(saved.warning);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Add event" onClose={onClose} wide>
      <div className="grid gap-3.5">
        {opts.importable.length > 0 && (
          <Choice
            label="Import from Upcoming"
            value={importId}
            options={[{ value: "", label: "—" }, ...opts.importable.map((g) => ({ value: String(g.id), label: g.label }))]}
            onChange={(v) => {
              setImportId(v);
              importRef.current = v;
              setLive(null);
              const g = opts.importable.find((x) => String(x.id) === v);
              if (!g) return setDraft(EMPTY);
              setDate(g.event_date);
              setGameType(g.game_type);
              setDraft(draftFrom(g));
              // An already-played game imports with ESPN's score pre-filled.
              if (g.espn_event_id && opts.espn_leagues.includes(g.league)) {
                fetchEspnEvent(g.league, g.espn_event_id)
                  .then((game) => {
                    if (!game || importRef.current !== v) return;
                    setLive(game);
                    setDraft((d) => ({
                      ...d,
                      home_score: String(game.home_score ?? 0),
                      away_score: String(game.away_score ?? 0),
                      has_score: game.has_final_score,
                    }));
                  })
                  .catch((e: unknown) => {
                    if (importRef.current === v) setError(`ESPN lookup failed, enter the score by hand: ${e instanceof Error ? e.message : String(e)}`);
                  });
              }
            }}
          />
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <Text label="Date" type="date" value={date} onChange={(v) => { setDate(v); setResults(null); setPicked(null); }} />
          {imported ? (
            <Text label="League" value={imported.league} onChange={() => {}} />
          ) : (
            <Choice
              label="League"
              value={league}
              options={[...opts.leagues.map((l) => ({ value: l, label: l })), { value: NEW_LEAGUE, label: "Add a new league…" }]}
              onChange={(v) => { setLeague(v); setResults(null); setPicked(null); }}
            />
          )}
          {league === NEW_LEAGUE && !imported && (
            <>
              <Text label="New league name" value={newLeague} onChange={setNewLeague} />
              <Choice label="Sport" value={sport} options={opts.sports.map((s) => ({ value: s, label: s }))} onChange={setSport} />
            </>
          )}
          <Choice label="Game type" value={gameType} options={opts.game_types.map((t) => ({ value: t, label: t }))} onChange={setGameType} />
          <Choice label="Special event" value={special} options={opts.specials} onChange={setSpecial} />
        </div>

        {!imported && espnOk && !manual && (
          <div className="grid gap-2">
            <button type="button" disabled={busy} onClick={search} className="justify-self-start rounded-lg border border-line px-3.5 py-2 text-sm">
              Search ESPN for games on this date
            </button>
            {results && results.length === 0 && <p className="text-sm text-muted">No ESPN games that day. Enter it by hand below.</p>}
            {results?.map((g) => (
              <button
                key={g.espn_event_id}
                type="button"
                aria-pressed={picked?.espn_event_id === g.espn_event_id}
                onClick={() => { setPicked(g); setDraft(draftFrom(g)); }}
                className={`rounded-lg border px-3 py-2 text-left text-sm ${picked?.espn_event_id === g.espn_event_id ? "border-accent bg-accent/10" : "border-line"}`}
              >
                {espnLabel(g)}
              </button>
            ))}
            <button type="button" onClick={() => { setManual(true); setPicked(null); setDraft(EMPTY); }} className="justify-self-start text-sm text-accent">
              Not on ESPN? Enter it by hand
            </button>
          </div>
        )}
        {!imported && !espnOk && league !== NEW_LEAGUE && (
          <p className="text-sm text-muted">ESPN doesn't cover {league}, so this one is entered by hand.</p>
        )}

        {imported && live && (
          <p className="text-sm text-muted">
            {live.status === "final"
              ? `ESPN has this final: ${live.away_team} ${live.away_score ?? "-"} - ${live.home_score ?? "-"} ${live.home_team}`
              : `ESPN status: ${live.status.replace("_", " ")}`}
          </p>
        )}
        {showForm && <GameFields d={draft} set={setDraft} />}
        {error && <p className="text-sm text-bad">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !showForm}
            onClick={save}
            className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground disabled:opacity-40"
          >
            {imported ? "Log this game" : "Save event"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
